import { useState, type FormEvent } from "react";
import type { NatwestApplicationWorkspace as Model } from "@myfm/domain";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateOnly, formatTimestampDate } from "../dates";
import { useRecordDraft } from "../ui/RecordDraftBoundary";
import { confirmDiscard } from "../ui/confirm-action";
import { Link } from "../ui/ScopedLink";
import type { NatwestCommand } from "./NatwestApplication";

export function NatwestBankProgress({
  model: m,
  command,
}: {
  model: Model;
  command: NatwestCommand;
}) {
  const sent = m.packages.filter((p) => p.sentAt);
  const [editing, setEditing] = useState(false),
    [mode, setMode] = useState("Bank response"),
    [versionId, setVersionId] = useState(sent[0]?.id ?? ""),
    [requirementId, setRequirementId] = useState("");
  const [kind, setKind] = useState("Acknowledged"),
    [date, setDate] = useState(""),
    [doc, setDoc] = useState(""),
    [message, setMessage] = useState(""),
    [reference, setReference] = useState(""),
    [note, setNote] = useState("");
  const [bankId, setBankId] = useState(""),
    [title, setTitle] = useState(""),
    [checks, setChecks] = useState<Record<string, boolean>>({});
  useRecordDraft(editing);
  const version = sent.find((p) => p.id === versionId),
    opening = mode === "Account opened";
  const checkText = {
    productConfirmed: "NatWest S42 client-money product confirmed",
    trustAcknowledgementReviewed:
      "Bank trust/client-money acknowledgement reviewed",
    onlineVisibilityReviewed: "Account visible in online banking",
    mandateAccessReviewed: "Mandate and authorised access reviewed",
    identifiersReviewed:
      "Full bank details compared with the bank confirmation",
  };
  async function save(event: FormEvent) {
    event.preventDefault();
    const ok = await command(
      opening ? "opening" : "bank-event",
      opening
        ? {
            applicationVersionId: versionId,
            requirementId,
            documentVersionId: doc,
            bankAccountId: bankId || null,
            actualAccountTitle: title,
            openedOn: date,
            note,
            ...Object.fromEntries(
              Object.keys(checkText).map((k) => [k, checks[k] ?? false]),
            ),
          }
        : {
            applicationVersionId: versionId,
            requirementId: requirementId || null,
            kind,
            occurredOn: date,
            messageId: message || null,
            documentVersionId: doc || null,
            bankReference: reference,
            note,
          },
    );
    if (ok) {
      setEditing(false);
      setNote("");
      setDate("");
      setChecks({});
    }
  }
  return (
    <section aria-label="NatWest bank progress">
      <h3>Bank response and account opening</h3>
      <p>
        Bank acknowledgement, account opening and finance handover are separate
        steps. Use the existing bank-account follow-up above to assign the next
        chase and date.
      </p>
      <ul>
        {m.requirements
          .filter((r) => r.required)
          .map((r) => (
            <li key={r.id}>
              {r.name}: {r.stage}
              {r.bankAccountId &&
                ` · Actual title: ${m.banks.find((b) => b.id === r.bankAccountId)?.name ?? "Review bank record"}`}
            </li>
          ))}
      </ul>
      {m.events.length > 0 && (
        <details>
          <summary>Bank response history ({m.events.length})</summary>
          {m.events.map((e) => (
            <p key={e.id}>
              {formatDateOnly(e.occurredOn)} · {e.kind} ·{" "}
              {e.requirementId
                ? (m.requirements.find((r) => r.id === e.requirementId)?.name ??
                  "Retained account")
                : "Whole application"}
              <br />
              {e.note}
              {e.bankReference && ` · ${e.bankReference}`}
            </p>
          ))}
        </details>
      )}
      {m.openingReviews.length > 0 && (
        <details>
          <summary>Opening reviews</summary>
          {m.openingReviews.map((o) => (
            <p key={o.id}>
              {o.actualTitle} · Opened {formatDateOnly(o.openedOn)} ·{" "}
              {o.complete
                ? "Opening checks recorded; bank verification is separate"
                : "Opening checks outstanding"}
              <br />
              {o.note}
            </p>
          ))}
        </details>
      )}
      {m.canPrepare && sent.length > 0 && !editing && (
        <button
          onClick={() => {
            setVersionId(sent[0]!.id);
            setEditing(true);
          }}
        >
          Add bank response or account opening
        </button>
      )}
      {editing && (
        <form className="compact-form" onSubmit={save}>
          <label>
            Update
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option>Bank response</option>
              <option>Account opened</option>
            </select>
          </label>
          <label>
            Application
            <select
              required
              value={versionId}
              onChange={(e) => {
                setVersionId(e.target.value);
                setRequirementId("");
              }}
            >
              {sent.map((p) => (
                <option key={p.id} value={p.id}>
                  Application {p.versionNumber} ·{" "}
                  {p.snapshot.accounts.map((a) => a.name).join(", ")}
                </option>
              ))}
            </select>
          </label>
          <label>
            Account
            <select
              required={opening}
              value={requirementId}
              onChange={(e) => {
                setRequirementId(e.target.value);
                const r = version?.snapshot.accounts.find(
                  (a) => a.id === e.target.value,
                );
                setTitle(r?.name ?? "");
                const current = m.requirements.find(
                  (a) => a.id === e.target.value,
                );
                setBankId(current?.bankAccountId ?? "");
              }}
            >
              <option value="">Whole application</option>
              {version?.snapshot.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          {!opening && (
            <label>
              Bank response
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                {[
                  "Acknowledged",
                  "Further information required",
                  "Awaiting opening",
                  "Rejected",
                  "Withdrawn",
                ].map((k) => (
                  <option key={k}>{k}</option>
                ))}
              </select>
            </label>
          )}
          <label>
            {opening ? "Actual opening date" : "Bank response date"}
            <DateOnlyInput required value={date} onChange={setDate} />
          </label>
          <label>
            {opening ? "Bank opening confirmation PDF" : "Bank response PDF"}
            <select
              required={opening || !message}
              value={doc}
              onChange={(e) => setDoc(e.target.value)}
            >
              <option value="">Choose retained bank document</option>
              {m.documents.map((d) => (
                <option key={d.versionId} value={d.versionId}>
                  {d.fileName}
                </option>
              ))}
            </select>
          </label>
          {!opening && (
            <>
              <label>
                Or retained bank reply
                <select
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                >
                  <option value="">No email selected</option>
                  {m.messages
                    .filter((msg) => msg.threadId === version?.threadId)
                    .map((msg) => (
                      <option key={msg.id} value={msg.id}>
                        {msg.subject} · {formatTimestampDate(msg.occurredAt)}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Bank reference
                <input
                  maxLength={200}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </label>
            </>
          )}
          {opening && (
            <>
              <label>
                Actual account title on the bank confirmation
                <input
                  required
                  minLength={2}
                  maxLength={120}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label>
                Bank record
                <select
                  value={bankId}
                  onChange={(e) => setBankId(e.target.value)}
                >
                  <option value="">Register the evidenced new account</option>
                  {m.banks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} · {b.verification}
                    </option>
                  ))}
                </select>
              </label>
              <p>
                A new record starts unverified. Full identifiers and independent
                verification use the existing bank records. No balance, feed or
                posting is created.
              </p>
              {Object.entries(checkText).map(([key, text]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={checks[key] ?? false}
                    onChange={(e) =>
                      setChecks({ ...checks, [key]: e.target.checked })
                    }
                  />
                  {text}
                </label>
              ))}
              <p>
                <Link
                  to={`/buildings/${m.building}/setup/financials?section=bank`}
                >
                  Review bank details and verification
                </Link>
              </p>
            </>
          )}
          <label>
            Outcome and next step
            <textarea
              required
              minLength={3}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <button>Save bank progress</button>
          <button
            type="button"
            className="secondary"
            onClick={async () => {
              if (!note && !date) setEditing(false);
              else if (await confirmDiscard("this bank update"))
                setEditing(false);
            }}
          >
            Discard update
          </button>
        </form>
      )}
    </section>
  );
}
